import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';

import { useGetConfigTemplatesQuery } from '@/features/environments/api/environmentsApi';

interface TemplateDialogProps {
  open: boolean;
  onClose: () => void;
  selectedTemplate: string;
  onTemplateChange: (template: string) => void;
}

/**
 * Dialog for selecting a template for the recipe
 * (Tech-support users only)
 */
export const TemplateDialog: React.FC<TemplateDialogProps> = ({
  open,
  onClose,
  selectedTemplate,
  onTemplateChange,
}) => {
  const [localTemplate, setLocalTemplate] = useState(selectedTemplate);

  const { data: templates = [], isLoading } = useGetConfigTemplatesQuery();

  // Sync local state with prop when dialog opens
  useEffect(() => {
    if (open) {
      setLocalTemplate(selectedTemplate);
    }
  }, [open, selectedTemplate]);

  const handleSave = () => {
    onTemplateChange(localTemplate);
    onClose();
  };

  const handleClear = () => {
    setLocalTemplate('');
    onTemplateChange('');
    onClose();
  };

  const sortedTemplates = [...templates].sort((a, b) => a.name.localeCompare(b.name));

  // Prevent closing on backdrop click or escape key
  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>Select Template</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="template-select-label">Template</InputLabel>
            <Select
              labelId="template-select-label"
              value={localTemplate}
              onChange={(e) => setLocalTemplate(e.target.value)}
              label="Template"
              disabled={isLoading}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {sortedTemplates.map((template) => (
                <MenuItem key={template.name} value={template.name}>
                  {template.name.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear} color="error" disabled={!localTemplate}>
          Clear
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
